---
title: FOI View Module
sidebar_position: 3
description: FOI View Module
---

# FOI View Module - Detailed Analysis

The **FOI View Module** is the primary workspace for analyzing Features of Interest. It provides a comprehensive set of widgets and tools for examining satellite imagery, signals, markers, and field attributes.

## FOI View Layout Overview

The FOI View is organized into three main columns plus bottom panels:

- **Left Column**: FOI List/Table provides quick navigation between Features of Interest without leaving the detailed analysis view.
- **Middle Column**: Observations and Map widgets for imagery analysis
- **Right Column**: FOI Attributes and Markers tabs
- **Bottom Panels**: Signal/Marker Visualization and Timelapse widgets

**Note**: For FOI list features, see [FOI List/Table Widget](02-getting-started.mdx#foi-listtable-widget). For map navigation, widget features, and Go To functionality, see [Map Widget and Navigation](02-getting-started.mdx#map-widget-and-navigation).

![FOI View full layout](../imgs/foi-view.png)

## Observations Widget

The **Observations Widget** displays satellite image chips captured over time for the selected FOI.

### Purpose

- View satellite imagery timeline for the FOI
- Monitor land-cover changes through chronological sequence
- Identify specific dates of interest for closer examination

### Configuration Options

All observation settings are accessed through the **options panel in the observations footer** (the gear or settings icon at the bottom of the observations panel). There is no separate Settings drawer for these controls.

#### Observation Layer

**Select Observation Layer**:

1. Open the **observations footer options**
2. Find the **Layer** dropdown
3. Select one or more layers (e.g., "S2 True Color", "S2 NDVI", "S2 False Color")
4. Observations and map imagery update immediately

**Available Layers** are scope-specific. Common examples:

- **S2 True Color**: Natural color imagery
- **S2 False Color**: Enhanced vegetation visualization
- **S2 NDVI**: Vegetation health index
- **S2 EVI, LAI, CCC**: Other vegetation indices
- **Custom layers**: Project-specific visualizations

**Use Cases**:

- Switch to NDVI to assess vegetation health
- Use True Color for natural appearance
- Use False Color to highlight vegetation density

#### Timerange

Time range (start and end date) is a global app setting. Open the **side panel** and adjust the **time range** there. Only observations within this range will be displayed. If you see no images, check that your time range matches data availability.

**Date Selection**:

- Click on an observation
- Sets the general settings **Selected date** timestamp
- Related widgets (Map imagery, Signals & Markers widget, timelapse) update to show that specific date

#### Layout Options

- **Images per row**: Adjust how many observations appear in a single row
  - Fewer columns = larger images
  - More columns = more images visible at once, but smaller
  - Disabled when **Week View** is on
- **Week View**: When enabled, each row represents one calendar week, making it easier to spot weekly patterns. When disabled, images fill rows based on the images-per-row setting.

#### Valid Signals

**Toggle Valid Signals Only**:

- Check **Show only valid observations**
- Hides images with low-quality data (clouds, shadows, processing errors)
- Unchecked shows all available observations

**Select Valid Signals per Source**:

- For each imagery data source (collection) select valid signal type

#### Padding

- Control the buffer of visible area around the FOI
- More padding shows more context around the field
- Less padding focuses closely on the FOI boundary

#### Geometry Overlay

- When enabled, the FOI boundary is drawn over each observation chip so you can see which area the imagery covers
- Toggle off to see the full chip without the boundary overlay

#### Pin Signal Values

- Under **Pinned signals**, select a signal type per collection to display its value on each observation card
- Values appear as tags in chip header
- Helps quickly identify signal trends without switching to the graph
- Set to **None** to disable pinning for that collection

![Pinned Signals](../imgs/observations-pinned-signals.png)

### Interactions

**Click to Enlarge**:

1. Click the expand icon for any image chip in the observations panel
2. The image opens in a **draggable, resizable pop-up window**
3. In the pop-up:
   - Drag the window to reposition
   - Resize using corner handles
   - Close using the X button
4. You can enlarge multiple observations at once and compare them side by side

![Enlarge observations](../imgs/observations-enlarge.png)

**Navigate Through Timeline**:

- Scroll through the observations panel to see all available dates
- Images are ordered chronologically

## FOI Attributes Widget

The **FOI Attributes Widget** displays general reference information about the selected FOI.

### Purpose

- View FOI metadata and characteristics
- Understand field properties (crop, size, location, etc.)
- Access reference attributes for analysis

### Content

The attributes displayed may include:

- **FOI ID**: System generated and unique fully qualified identifier (in format `<SCOPE>.FOI.<ID>`)
- **Reference ID**: Reference ID (client provided IDs unique within scope FOI type; what users usually identify FOIs by)
- **FOI Type**: FOI classification (field, parcel, etc.)
- **Crop Type**: What crop is declared or detected
- **Land Use**: Agricultural category
- **Area**: Field size
- **Additional Attributes**: Project-specific metadata

**Note**: Available attributes depend on scope configuration and data sources. The exact set of attributes shown is defined by each scope's data, so different scopes will expose different attributes.

## Markers Widget

The **Markers Widget** displays predictive measurements indicating management patterns and field characteristics.

Markers are derived from signal phenology patterns and provide insights into:

- Crop classification predictions
- Bare soil detection
- Mowing events
- Homogeneity analysis
- And more

The Markers Widget has four tabs: **Summary**, **Decisions**, **Evaluations**, and **Validation** (Validation appears only in the Validation Console).

### Summary Tab

The **Summary** tab provides a high-level overview of analytical markers. Decision and evaluation markers are **not shown here** — they each have their own dedicated tab.

![Markers Summary](../imgs/markers-summary.png)

**Displayed Information**:

1. **Classifications**:
   - Predicted crop category (e.g., "Cereals", "Root crops")
   - Confidence score displayed as percentage
   - Hover over the row to see **"Classified as"** and **"Declared as"** tooltips
   - See [Crop group and land use Classification Detailed View](#crop-group-and-land-use-classification-detailed-view) and [Distance and Similarity Detailed View](#distance-and-similarity-detailed-view)

2. **NDVI Mean**:
   - Average NDVI value over the analysis period
   - Indicates overall vegetation health
   - Numerical value and interpretation

3. **Predicted Homogeneity**:
   - Assessment of field uniformity using the homogeneous score
   - Confidence score
   - Indicates whether the field is uniform or mixed

4. **Observations** (e.g., bare soil detections):
   - **Number of detections** within the time period
   - Dates when observed
   - Helps identify cultivation events

5. **Detected Events** (e.g., mowing):
   - **Count of events** identified, with rise and fall event counts shown directly in the summary row (e.g., "2 | 1" for 2 rise and 1 fall)
   - Dates of detected events

Each row is expandable — click a row to see the full detail view.

#### Crop group and land use Classification Detailed View

These sub-views show detailed classification scores.

**Features**:

- **Graphical Visualization**: Bar charts showing confidence scores for each class
- **Predicted Group Highlighted in Green**: The highest-confidence prediction
- **Declared Group (if conflicting) in Blue**: Shows if the declared crop differs from prediction
- **All Classes Shown**: Complete breakdown of classification probabilities

**How to Read**:

1. Look for the **green bar** — this is the predicted class
2. Check the **confidence percentage** — higher is more certain
3. If there's a **blue bar**, that's the declared crop (if different)
4. Compare predicted vs. declared to identify potential mismatches

#### Distance and Similarity Detailed View

The **Distance** and **Similarity** markers both compare the FOI's signal time-series (its spectral profile over the season) against reference profiles for each candidate crop, to judge how well the FOI matches its declared crop and which crop it most resembles. They use the **same bar-chart layout** as the classification views — but they measure the match in opposite directions, so their charts are **sorted oppositely**. Each view shows the **classified (best-matching) class with its score** and the **declared class with its score**; the best-matching class is highlighted in **green**, and the **declared class is shown in blue** when it differs from the best match.

**Distance marker**

- **What it measures**: how spectrally _different_ the FOI is from the reference profile of each class (a statistical distance). **Lower = closer = more similar.**
- **How to read it**: scores are sorted **lowest-first**, so the **top bar is the closest (best) match**. A low score on the **declared** class means the FOI looks like its declared crop; a high score on the declared class means it looks different from it.

**Similarity marker**

- **What it measures**: how closely the FOI's spectral signature _matches_ the reference profile of each class. **Higher = more similar.**
- **How to read it**: scores are sorted **highest-first**, so the **top bar is the best match**. If the declared class does not appear near the top, the FOI's profile diverges from what's expected for that crop.

**Interpretation**:

- A strong match to a crop **other than the declared one** (low Distance or high Similarity to a different class) may indicate a misclassification or an incorrect declaration — flag for review.
- A weak match to the **declared** crop (high Distance or low Similarity on the declared class) is a signal to inspect the FOI more closely.
- **Common confusion**: the two markers look identical but are read in opposite ways — a **low Distance** score is a good match, whereas a **high Similarity** score is a good match.

---

### Decisions Tab

The **Decisions** tab shows all decision markers for the selected FOI. Decision markers use a configurable decision tree that combines multiple inputs to assess compliance with business rules.

![Markers - Decisions](../imgs/markers-decisions.png)

Markers are displayed with their **human-readable names** (not raw internal codes). Each row shows the traffic-light result:

- **Green**: Approved / Compliant
- **Yellow**: Warning / Requires attention
- **Red**: Rejected / Non-compliant

**Decision Diagram**:

1. Click a decision marker row to expand it
2. The full decision tree diagram appears as an image that visualises the logic path producing the decision result
3. The diagram is static — there are no interactive tooltips or hover states on the nodes

### Evaluations Tab

The **Evaluations** tab shows evaluation markers for the selected FOI with boolean (yes/no) answers.

![Markers - Evaluations](../imgs/markers-evaluations.png)

Markers are displayed with their **human-readable names**. Rows are not expandable. If there's not enough space to display whole evaluation question in a row, the question is cut with ... and whole question is displayed in tooltip.

## Signals and Markers Visualization Widget

The **Signals and Markers Visualization Widget** displays a time-series graph of signals and markers for the selected FOI. Which signals and markers are displayed in this widget is defined in scope configuration (see [Scope Configuration](05-scope-configuration.mdx)).

![Signals Markers Visualization Widget](../imgs/sig-ma-viz.png)

### Purpose

- Visualize signal and marker data over time
- Identify trends and patterns
- Correlate signals with imagery and markers

### Features

#### Display Signals and Markers

- Available and configured signals and markers for the chosen FOI and marker context
- Signals shown as continuous lines
- Markers shown as points or discrete indicators
- Color-coded for easy identification
- **FOI reference data** (date-based attributes configured per scope) appear as **static vertical band markers** in the chart

#### Interactive Legend and Controls

The controls panel is attached to the side of the chart (default: right). It contains:

- **Colored checkboxes**: Toggle visibility of each signal or marker on the graph
- **Tooltip**: Toggle whether a signal/marker appears in the data tooltip by clicking on signal/marker label - **bold** labels indicate signal/marker will be displayed in tooltip
- **Options menu**: Adjust tooltip placement and move the controls panel to a different position (left, right, top, bottom)

#### Tooltip Placement Options

- **Cursor**: Tooltip follows the mouse
- **Top, Bottom, Left, Right**: Fixed position relative to graph
- **Corners**: Top-left, top-right, bottom-left, bottom-right

#### Date Selection

- Click on a date on the graph
- Sets the general settings **Selected date** timestamp
- Related widgets (Map imagery, observations, timelapse) update to show that specific date
- Correlate signals with spatial imagery

#### Multi-Axis Support (if enabled)

- Signals may be assigned to different Y-axes
- Up to 3 axes (left, right, or both)
- Allows comparing signals with different value ranges
- Axis labels and scales shown on graph edges

## Timelapse Widget

The **Timelapse Widget** creates an animated visualization of the FOI's observations over time.

### Purpose

- View dynamic changes over time in an animation
- Identify temporal patterns visually
- Create shareable GIF animations for reports

![Timelapse Widget](../imgs/timelapse.png)

### Controls

#### Play Animation

1. Click the **Play** button
2. Observations play in chronological sequence
3. Animation loops continuously or stops at the end (depending on settings)
4. Click **Pause** to stop playback

#### Step Through Image Chips

- Use **Previous** and **Next** buttons to manually step through observations
- Advance one image at a time
- Go backwards to review specific dates

#### Playback Speed

- Adjust the animation speed
- Slower for detailed examination
- Faster for overview

#### Layer Selection

- Choose which observation layer to animate (same as Observations Widget)
- Timelapse reflects the currently selected layer
- Toggle which geometries to display (original FOI boundary, pixelated FOI geometry)

#### Download Animation as GIF

1. Click the **Download GIF** button
2. The timelapse is rendered and saved as a GIF file
3. Use the GIF in presentations, reports, or external tools
